import { START_REGENERATE_RUN } from "@lib/graphql/mutations";
import { REGENERATE_PREVIEW_QUERY } from "@lib/graphql/queries";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";

export interface StaleItem {
  kind: string;
  name: string;
  reason: string;
  upstreamKind: string;
  upstreamLabel: string;
  pinnedVersionId: string | null;
  currentVersionId: string | null;
  userEdited: boolean;
  artefactId: string;
  selectionStepContinuityId: string | null;
}

const REASON_LABEL: Record<string, string> = {
  brief_changed: "Brief has been edited since this was generated",
  blueprint_drift: "Role blueprint has changed",
  dimensions_edited: "Capability dimensions edited since last lock",
  matrix_changed: "Assessment matrix has changed",
};

interface Props {
  open: boolean;
  campaignId: string;
  /** Restrict the preview to these kinds. Omit to preview every stale kind on the campaign. */
  kinds: string[];
  onClose: () => void;
}

// The modal is preview-and-start only. Live progress lives in the top-of-UI
// background-tasks indicator — that's the canonical place for run state, and
// duplicating it here would just drift. After a successful start the modal
// closes and points the user there.
export function RegenerateModal({ open, campaignId, kinds, onClose }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [{ data, fetching: previewFetching }] = useQuery({
    query: REGENERATE_PREVIEW_QUERY,
    variables: { id: campaignId, kinds },
    pause: !open,
    requestPolicy: "network-only",
  });
  const [{ fetching: starting }, startRun] = useMutation(START_REGENERATE_RUN);

  const items: StaleItem[] = useMemo(() => {
    const raw = data?.campaign?.regeneratePreview?.items ?? [];
    return raw as StaleItem[];
  }, [data]);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setConfirmOverwrite(false);
    setSelected(new Set(items.map(itemKey)));
  }, [open, items]);

  if (!open) return null;

  const visibleItems = items.filter((i) => selected.has(itemKey(i)));
  const visibleManual = visibleItems.filter((i) => i.userEdited);

  async function handleStart() {
    setSubmitError(null);
    const enqueueKinds = Array.from(new Set(visibleItems.map((i) => i.kind)));
    const result = await startRun({
      input: { campaignId, kinds: enqueueKinds, confirmOverwrite },
    });
    if (result.error) {
      setSubmitError(result.error.message);
      return;
    }
    const payload = result.data?.startRegenerateRun;
    if (!payload) {
      setSubmitError("Unexpected empty response");
      return;
    }
    if (payload.status === "manual_edits_present") {
      setSubmitError('Some artefacts have manual edits — tick "Allow overwrite" to include them.');
      return;
    }
    if (payload.status === "invalid") {
      setSubmitError((payload.errors ?? []).join("; ") || "Could not start regeneration.");
      return;
    }
    // "ok", "already_running", "nothing_to_do" — close and let the
    // background-tasks indicator at the top of the page take over. If a run
    // was already in flight, that indicator already shows it.
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-text-primary">Regenerate stale artefacts</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          {previewFetching ? (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <Loader2 size={14} className="animate-spin" />
              Loading preview…
            </div>
          ) : items.length === 0 ? (
            <p data-testid="regenerate-no-items" className="text-sm text-text-secondary">
              Nothing to regenerate — everything is in sync.
            </p>
          ) : (
            <>
              <p className="text-sm text-text-secondary">
                {items.length} artefact{items.length === 1 ? "" : "s"} are out of date. Review what
                will be rebuilt before continuing — once started, progress is shown in the
                background-tasks indicator at the top of the page.
              </p>
              <ul
                className="mt-3 max-h-80 space-y-2 overflow-y-auto"
                data-testid="regenerate-preview-items"
              >
                {items.map((it) => {
                  const key = itemKey(it);
                  const isSelected = selected.has(key);
                  return (
                    <li
                      key={key}
                      className="flex items-start gap-3 rounded-md border border-border px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={isSelected}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(key);
                          else next.delete(key);
                          setSelected(next);
                        }}
                        aria-label={`Include ${it.name}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-text-primary">{it.name}</span>
                          {it.userEdited && (
                            <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                              <AlertTriangle size={10} /> Manual edits
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-secondary">
                          {REASON_LABEL[it.reason] ?? it.reason}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {visibleManual.length > 0 && (
                <label className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <input
                    type="checkbox"
                    checked={confirmOverwrite}
                    onChange={(e) => setConfirmOverwrite(e.target.checked)}
                  />
                  <span>
                    Allow overwrite of {visibleManual.length} manually-edited artefact
                    {visibleManual.length === 1 ? "" : "s"}. The current version stays in history
                    but is replaced as the current version.
                  </span>
                </label>
              )}
              {submitError && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">{submitError}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-card-hover px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={
              starting ||
              visibleItems.length === 0 ||
              (visibleManual.length > 0 && !confirmOverwrite)
            }
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            Start regeneration ({visibleItems.length})
          </button>
        </div>
      </div>
    </div>
  );
}

function itemKey(item: StaleItem): string {
  return `${item.kind}:${item.artefactId}`;
}
